import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject} from '@angular/core';
import {MfaActivationCode, MfaFinishPairingOrAuthResponse, MfaPairing, MfaPairingResponse} from '../../_data/mfa/mfa-pairing';
import {AuthenticationService} from '../../_services/authentication.service';
import {UntypedFormBuilder, UntypedFormGroup, Validators, ReactiveFormsModule} from '@angular/forms';
import {phoneValidator} from '../../_utils/validators';
import {MfaService} from '../../_services/data/mfa.service';
import {NotificationService} from '../../_services/gui/notification.service';
import {PingErrorId} from '../../_constants/types';
import {AppConfig} from '../../_data/config/app-config';
import {of, Subject} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {exists} from '../../_utils/operators';
import {IconProp} from '@fortawesome/fontawesome-svg-core';
import {faAt, faComments, faMobile, faPhone} from '@fortawesome/free-solid-svg-icons';
import {AddableDeviceComponent} from '../addable-device/addable-device.component';
import {NgClass, NgTemplateOutlet} from '@angular/common';
import {ValidationErrorsComponent} from '../../common/forms/validation-errors/validation-errors.component';
import {QRCodeComponent} from 'angularx-qrcode';
import {PhoneFormRowComponent} from '../../common/forms/phone-form-row/phone-form-row.component';
import {TranslateModule} from '@ngx-translate/core';

@Component({
    selector: 'app-add-mfa-device',
    templateUrl: './add-mfa-device.component.html',
    styleUrls: ['./add-mfa-device.component.scss'],
    imports: [AddableDeviceComponent, ReactiveFormsModule, NgClass, ValidationErrorsComponent, NgTemplateOutlet, QRCodeComponent, PhoneFormRowComponent, TranslateModule]
})
export class AddMfaDeviceComponent implements OnInit, OnDestroy {

  @Input() userName: string;

  @Output() added: EventEmitter<void> = new EventEmitter<void>();
  @Output() removed: EventEmitter<void> = new EventEmitter<void>();

  faAt = faAt as IconProp;
  faComments = faComments as IconProp;
  faPhone = faPhone as IconProp;
  faMobile = faMobile as IconProp;

  mfaActivationCode: MfaActivationCode;
  _showQrCode = false;
  elementType: any = 'canvas';
  errorCorrectionLevel: any = 'L';

  _showStartPairingForm = true;
  startPairingFormGroup: UntypedFormGroup;
  startPairingFormSubmitted = false;

  _showFinishPairingForm = false;
  finishPairingFormGroup: UntypedFormGroup;
  finishPairingFormSubmitted = false;

  enablePhoneMask = true;

  private _addingEmail: any;
  private _addingSms: any;
  private _addingPhone: any;
  private _addingApp: any;

  private emailFormGroup: UntypedFormGroup;
  private phoneFormGroup: UntypedFormGroup;

  private mfaPairing: MfaPairing;
  private pairingSessionId: string;

  private cancelAwaitApp$: Subject<void>;

  private readonly formBuilder = inject(UntypedFormBuilder);
  private readonly appConfig = inject(AppConfig);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly mfaService = inject(MfaService);
  private readonly notificationService = inject(NotificationService);

  private deviceAdded() {
    this.added.emit();
    //Trigger the same logic as clicking cancel (to hide the form and clear the form fields)
    this.cancel();
  }

  ngOnInit(): void {
    this.mfaPairing = null;
    this.showStartPairingForm = false;
    this.pairingSessionId = null;

    const phoneValidators = [Validators.required];
    if(this.appConfig.mfaPhonePreValidation) {
      phoneValidators.push(phoneValidator);
    } else {
      this.enablePhoneMask = false;
    }

    this.phoneFormGroup = this.formBuilder.group({phone: ['', Validators.compose(phoneValidators)]});
    this.emailFormGroup = this.formBuilder.group({email: ['', Validators.compose([Validators.required, Validators.email])]});
    this.finishPairingFormGroup = this.formBuilder.group({code: ['', Validators.required]});
  }

  ngOnDestroy(): void {
    this.cancelAwaitApp();
  }

  cancel() {
    //email, sms, and phone setters also clear the appropriate startPairing form value when set to false
    this.addingEmail = false;
    this.addingSms = false;
    this.addingPhone = false;
    this.addingApp = false;

    //Also clear the finish pairing form and the submitted booleans
    this.finishPairingFormGroup.controls.code.setValue('');
    this.startPairingFormSubmitted = false;
    this.finishPairingFormSubmitted = false;
  }

  cancelAwaitApp() {
    console.debug('Cancelling Ping await');
    this.cancelAwaitApp$?.next();
    this.cancelAwaitApp$?.complete();
  }

  startOfflinePairing(value) {
    this.startPairingFormSubmitted = true;
    if(this.startPairingFormGroup.valid) {
      console.debug(`Starting offline pairing with ${JSON.stringify(value)}...`);
      if(this.mfaPairing.pairingMethod === 'EMAIL') {
        this.mfaPairing.userMFADetails.email = value.email;
      } else {
        this.mfaPairing.userMFADetails.phoneNumber = value.phone;
      }
      console.debug(`MfaPairing: ${JSON.stringify(this.mfaPairing)}...`);
      this.mfaService.startOfflinePairing(this.mfaPairing).subscribe((mfaPairingResponse: MfaPairingResponse) => {
        console.debug(`Start pairing response: ${JSON.stringify(mfaPairingResponse)}`);
        if(PingErrorId.ok === mfaPairingResponse.errorId) {
          this.showStartPairingForm = false;
          this.showFinishPairingForm = true;
          this.pairingSessionId = mfaPairingResponse.sessionId;
        } else if(PingErrorId.maxDevices === mfaPairingResponse.errorId) {
          this.notificationService.displayI18n('content.mfa-max-devices');
          this.cancel();
        } else if(PingErrorId.deviceAlreadyInUse === mfaPairingResponse.errorId) {
          this.notificationService.displayI18n('content.mfa-device-in-use');
          this.cancel();
        } else if(PingErrorId.invalidPhone === mfaPairingResponse.errorId) {
          this.notificationService.displayI18n('content.phone-invalid');
          this.cancel();
        } else {
          this.notificationService.displayI18n('content.error-contact-admin');
          this.cancel();
        }
      });
    }
  }

  finishOfflinePairing(value) {
    this.finishPairingFormSubmitted = true;
    if(this.finishPairingFormGroup.valid) {
      console.debug(`Finishing pairing ${JSON.stringify(value)}...`);
      const mfaFinishPairing = {
        userMFADetails: {userName: this.userName},
        sessionId:      this.pairingSessionId,
        otpCode:        value.code
      };
      this.mfaService.finishOfflinePairing(mfaFinishPairing)
        .subscribe((mfaFinishPairingResponse: MfaFinishPairingOrAuthResponse) => {
          if(PingErrorId.ok === mfaFinishPairingResponse.errorId) {
            this.notificationService.displayI18n('content.mfa-device-added');
            this.deviceAdded();
          } else if(PingErrorId.incorrectCode === mfaFinishPairingResponse.errorId || PingErrorId.incorrectPassword === mfaFinishPairingResponse.errorId) {
            this.finishPairingFormGroup.controls.code.setErrors({mfaWrongCode: true});
          }
        });
    }
  }

  addEmail() {
    this.mfaPairing = {pairingMethod: 'EMAIL', userMFADetails: {userName: this.userName, email: ''}};
    this.startPairingFormGroup = this.emailFormGroup;
    this.addingEmail = true;
    this.showStartPairingForm = true;
  }

  addSms() {
    this.mfaPairing = {pairingMethod: 'SMS', userMFADetails: {userName: this.userName, phoneNumber: ''}};
    this.startPairingFormGroup = this.phoneFormGroup;
    this.addingSms = true;
    this.showStartPairingForm = true;
  }

  addPhone() {
    this.mfaPairing = {pairingMethod: 'VOICE', userMFADetails: {userName: this.userName, phoneNumber: ''}};
    this.startPairingFormGroup = this.phoneFormGroup;
    this.addingPhone = true;
    this.showStartPairingForm = true;
  }

  addPingApp() {

    this.addingApp = true;

    if(this._showQrCode) {
      //Already showing the qr code.
      return;
    }

    this.mfaService.requestQrCode({userName: this.userName}).pipe(
      // Handle when a user already has the max devices and tries to add the Ping app.
      catchError(error => {
        if(error.status === 409) {
          this.notificationService.displayI18n('content.mfa-max-devices');
          this.cancel();
        } else {
          console.warn('Unexpected error', error);
        }
        return of(null);
      }),
      exists()
    ).subscribe((mfaActivationCode: MfaActivationCode) => {
      this.mfaActivationCode = mfaActivationCode;
      this.showQrCode = true;
      //Monitor the activation code for when the user is done pairing the app on their phone.
      this.cancelAwaitApp$ = new Subject<void>();
      this.mfaService.awaitPaired(mfaActivationCode.activationCode, this.userName, this.cancelAwaitApp$).subscribe(() => {
        console.debug('Await paired complete');
        this.notificationService.displayI18n('content.mfa-device-added');
        this.deviceAdded();
      });
    });
  }

  set showStartPairingForm(value: boolean) {
    this._showStartPairingForm = value;
    if(value) {
      this.showQrCode = false;
      this.showFinishPairingForm = false;
    }
  }

  set showQrCode(value: boolean) {
    this._showQrCode = value;
    if(value) {
      this.showStartPairingForm = false;
      this.showFinishPairingForm = false;
    } else {
      //Cancel monitoring any activation code.
      this.cancelAwaitApp();
    }
  }

  set showFinishPairingForm(value: boolean) {
    this._showFinishPairingForm = value;
    if(value) {
      this.showStartPairingForm = false;
      this.showQrCode = false;
    }
  }

  get addingEmail(): any {
    return this._addingEmail;
  }

  set addingEmail(value: any) {
    this._addingEmail = value;
    if(value) {
      this._addingPhone = false;
      this._addingSms = false;
      this._addingApp = false;
    } else {
      this.emailFormGroup.controls.email.setValue('');
    }
  }

  get addingSms(): any {
    return this._addingSms;
  }

  set addingSms(value: any) {
    this._addingSms = value;
    if(value) {
      this._addingPhone = false;
      this._addingEmail = false;
      this._addingApp = false;
    } else {
      this.phoneFormGroup.controls.phone.setValue('');
    }
  }

  get addingPhone(): any {
    return this._addingPhone;
  }

  set addingPhone(value: any) {
    this._addingPhone = value;
    if(value) {
      this._addingEmail = false;
      this._addingSms = false;
      this._addingApp = false;
    } else {
      this.phoneFormGroup.controls.phone.setValue('');
    }
  }

  get addingApp(): any {
    return this._addingApp;
  }

  set addingApp(value: any) {
    this._addingApp = value;
    if(value) {
      this._addingPhone = false;
      this._addingSms = false;
      this._addingEmail = false;
      this._showQrCode = false;
    } else {
      //Make sure to stop any polling that's checking if the app is paired
      this.cancelAwaitApp();
    }
  }

}
